import React, { useState, useRef } from 'react';
import { Modal, Button, Typography, Grid, Card, CardContent, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import {
  BusinessProfileNotes,
  createBusinessProfileNotes,
} from '../../slices/business-profile/businessProfileSlice';
import styles from './CreateNoteModal.module.scss';
import NoteInput from '../form-inputs/note-input/NoteInput';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectUser } from '../../slices/user/userSlice';
import { NWClient } from '../../client/NWClient';
import { fetchEmployeeNotes } from '../../slices/employees/employeesSlice';
import { fetchCustomerNotes } from '../../slices/customers/customersSlice';
import { fetchSupplierNotes } from '../../slices/suppliers/suppliersSlice';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

interface CreateNoteModalProps {
  companyId?: number;
  employeeId?: number;
  customerId?: number;
  supplierId?: number;
  open: boolean;
  onClose: () => void;
}

export default function CreateNoteModal({
  companyId,
  employeeId,
  customerId,
  supplierId,
  open,
  onClose,
}: CreateNoteModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLDivElement | null>(null);
  const currentUser = useAppSelector(selectUser);

  const [formData, setFormData] = useState({
    company: companyId,
    user: currentUser.id,
    note: '',
  });

  const [empFormData, setEmpFormData] = useState({
    employee_id: employeeId,
    note: '',
  });

  const [cusFormData, setCusFormData] = useState({
    customer_id: customerId,
    user: currentUser.id,
    notes: '',
  });

  const [supFormData, setSupFormData] = useState({
    supplier_id: supplierId,
    user: currentUser.id,
    notes: '',
  });

  const handleChange =
    <K extends keyof typeof formData>(field: K) =>
    (value: (typeof formData)[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleEmpChange =
    <K extends keyof typeof empFormData>(field: K) =>
    (value: (typeof empFormData)[K]) => {
      setEmpFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleCusChange =
    <K extends keyof typeof cusFormData>(field: K) =>
    (value: (typeof cusFormData)[K]) => {
      setCusFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleSupChange =
    <K extends keyof typeof supFormData>(field: K) =>
    (value: (typeof supFormData)[K]) => {
      setSupFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleCreate = () => {
    dispatch(showOverlay());
    if (companyId) {
      dispatch(
        createBusinessProfileNotes({
          companyId: companyId,
          data: formData as Omit<BusinessProfileNotes, 'id'>,
        })
      )
        .unwrap()
        .finally(() => {
          onClose();
          dispatch(hideOverlay());
        });
    }

    if (employeeId) {
      NWClient.post(`employee/${employeeId}/notes`, empFormData, true).then(() => {
        dispatch(hideOverlay());
        dispatch(fetchEmployeeNotes(employeeId));
      });
      onClose();
    }

    if (customerId) {
      NWClient.post(`customer-notes`, cusFormData, true).then(() => {
        dispatch(hideOverlay());
        dispatch(fetchCustomerNotes(customerId));
      });
      onClose();
    }

    if (supplierId) {
      NWClient.post(`supplier-notes`, supFormData, true).then(() => {
        dispatch(hideOverlay());
        dispatch(fetchSupplierNotes(supplierId));
      });
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Grid component='div' container className={styles.modal_container}>
        <Grid component='div' size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
          <Card ref={formRef}>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <Typography variant='h6' gutterBottom>
                {t('businessProfile.createNote')}
              </Typography>

              <Grid component='div' container spacing={2}>
                <Grid component='div' size={{ xs: 12 }}>
                  {companyId && (
                    <NoteInput
                      value={formData.note}
                      onChange={handleChange('note')}
                      disabled={false}
                    />
                  )}
                  {employeeId && (
                    <NoteInput
                      value={empFormData.note}
                      onChange={handleEmpChange('note')}
                      disabled={false}
                    />
                  )}
                  {customerId && (
                    <NoteInput
                      value={cusFormData.notes}
                      onChange={handleCusChange('notes')}
                      disabled={false}
                    />
                  )}
                  {supplierId && (
                    <NoteInput
                      value={supFormData.notes}
                      onChange={handleSupChange('notes')}
                      disabled={false}
                    />
                  )}
                </Grid>
              </Grid>

              <Stack
                component='div'
                direction='row'
                sx={{ marginTop: '16px' }}
                style={{ gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}
              >
                <Button
                  id='createNoteModalCloseBtn'
                  type='button'
                  variant='contained'
                  className='btn-default btn-action-basic btn-cancel'
                  onClick={onClose}
                >
                  {t('buttons.close')}
                </Button>
                <Button
                  id='createNoteModalCreateBtn'
                  type='button'
                  variant='contained'
                  className='btn-default btn-action-basic'
                  onClick={() => handleCreate()}
                >
                  {t('buttons.create')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Modal>
  );
}
